/**
 * @file pickerArchived.js
 * @version 1.0.0
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2026-10-28
 * @modified 2026-10-28
 * @description Archived products in the Item picker's side panel ("Recently
 *              ordered" / "Recently bought", "Contracted", "On linked POs").
 *
 *              Cam, 28 Oct 2026: "Item picker in create order (and wherever
 *              else this is applicable) shows archived items in the recent
 *              orders side, but should be faded and with an archived badge".
 *
 *              The archive is this system's (inventory.items.is_archived —
 *              CLAUDE.md, "Xero mirroring: contacts vs items"), and an
 *              archived product is not to be ordered from a picker: the list
 *              hides it until "Show archived" is turned on, a standing order
 *              refuses one. So a side-panel row whose product is archived is
 *              shown faded with an "Archived" badge and cannot be chosen —
 *              EXCEPT:
 *
 *                - the line's own item (an older document names it; choosing
 *                  it again changes nothing, and never strips the line);
 *                - a row the screen says may be chosen (`allowArchived`): a
 *                  bill's "On linked POs" — the bill bills what the PO bought,
 *                  item and all, even when the product was archived since.
 *
 *              Pure, so the rule is tested without drawing the picker.
 */

/** The title of an archived row that cannot be chosen. */
export const ARCHIVED_PICK_TITLE = 'Archived — choose the product that replaces it';
/** The title of the line's own archived item. */
export const ARCHIVED_OWN_TITLE = 'Archived — already on this line';
/** The title of an archived row the screen lets be chosen (a bill's linked PO). */
export const ARCHIVED_LINKED_TITLE = 'Archived — on the linked purchase order, so the bill can name it';

const codeOf = (i) => String(i?.item_code ?? i?.product_code ?? '').trim();

/**
 * The item a side-panel row stands for, from the picker's own list: by item
 * id where the row has one, else by code — the active row where the code has
 * one, else an archived one (the rule every item lookup follows).
 *
 * @param {Array} items   the picker's items
 * @param {object} row    { inventory_item_id?, product_code? }
 * @returns {object|null}
 */
export function itemForPickerRow(items, row) {
  const list = Array.isArray(items) ? items : [];
  if (!row) return null;
  if (row.inventory_item_id != null) {
    const byId = list.find(i => i && i.id === row.inventory_item_id);
    if (byId) return byId;
  }
  const code = String(row.product_code ?? '').trim();
  if (!code) return null;
  const matches = list.filter(i => i && codeOf(i) === code);
  return matches.find(i => !i.is_archived) || matches[0] || null;
}

/**
 * How one side-panel row behaves.
 *
 * @param {object} args
 * @param {object|null} args.item      the item the row stands for
 * @param {boolean} [args.flagged]     the server said it is archived
 * @param {*} [args.value]             the line's own item id
 * @param {boolean} [args.allowArchived] this row may be chosen even archived
 * @returns {{ archived: boolean, blocked: boolean, title: string|null }}
 */
export function pickerRowState({ item, flagged = false, value = null, allowArchived = false } = {}) {
  const archived = !!(item?.is_archived || flagged);
  if (!archived) return { archived: false, blocked: false, title: null };
  const own = value != null && item != null && item.id === value;
  if (own) return { archived, blocked: false, title: ARCHIVED_OWN_TITLE };
  if (allowArchived) return { archived, blocked: false, title: ARCHIVED_LINKED_TITLE };
  return { archived, blocked: true, title: ARCHIVED_PICK_TITLE };
}
